<template>
  <SlideOver
    v-model="open"
    id="client-link-slide-over"
    :title="
      editing
        ? $t('components.client-link-slide-over.edit-client-link')
        : $t('components.client-link-slide-over.new-client-link')
    "
    :closeable="!loading && !isDeleteModalVisible"
    :loading-overlay="loading"
  >
    <Input
      v-model="editClientLink.outwards"
      id="outwards"
      :label="$t('settings.client-links.outwards-name')"
      :placeholder="$t('settings.client-links.outwards-placeholder')"
      required
    />

    <Input
      v-model="editClientLink.inwards"
      id="inwards"
      :label="$t('settings.client-links.inwards-name')"
      :placeholder="$t('settings.client-links.inwards-placeholder')"
      required
    />

    <Checkbox
      v-model="isAdvancedSelected"
      id="clientLinkIsAdvanced"
      :label="$t('settings.client-links.is-advanced')"
      :description="$t('settings.client-links.advanced-description')"
    />

    <TagsInput
      v-if="isAdvancedSelected"
      v-model="selectedCustomFields"
      :auto-complete-collection="DATABASE_AUTOCOMPLETE_COLLECTION.CustomField"
      :label="$t('settings.client-links.shared-custom-fields')"
      :placeholder="$t('general.select-custom-field')"
    />

    <template #footer>
      <Button
        v-if="editing"
        type="secondary"
        size="lg"
        icon-only
        :disabled="loading"
        @click="isDeleteModalVisible = true"
      >
        <TrashIcon class="size-4 text-red-500" aria-hidden="true" />
      </Button>
      <Button type="secondary" size="lg" :disabled="loading" @click="cancel">
        {{ $t('general.cancel') }}
      </Button>
      <Button
        size="lg"
        :loading="loading"
        :disabled="!changed"
       
        @click="saveClientLink"
      >
        {{
          $t(
            'components.client-link-slide-over.' +
              (editing ? 'save-link' : 'create-link')
          )
        }}
      </Button>
    </template>

    <ModalConfirmDelete
      v-model="isDeleteModalVisible"
      :title="$t('settings.client-links.delete-client-links')"
      :text="$t('settings.client-links.delete-client-links-text')"
      :loading="loadingDelete"
      @confirmed="deleteClientLink"
    />
  </SlideOver>
</template>

<script lang="ts">
export type CreateClientLink = Omit<ClientLink, '_id' | 'orgId'>;

export const defaultClientLink: CreateClientLink = {
  inwards: '',
  outwards: '',
};
</script>

<script lang="ts" setup>
import { isEqual } from 'lodash';
import { klona } from 'klona';
import { TrashIcon } from '@heroicons/vue/20/solid';
import type { ClientLink } from '@/types';
import { ErrorType } from '@/types/error';
import { useNotifications } from '~~/stores/notifications';
import { useUserStore } from '@/stores/user';
import {
  getGeneralErrorMap,
  getNotificationDataByMap,
  type ErrorMapItem,
} from '~/util/error';
import { useApiClientLink } from '~/composables/api-services/api-client-link';
import { DATABASE_AUTOCOMPLETE_COLLECTION } from '@/types';
import { getChangedOrUndefined } from '~/util/compare-items';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  clientLink: {
    type: Object as PropType<ClientLink | CreateClientLink>,
    default: () => defaultClientLink,
  },
});

const editClientLink = ref<ClientLink | CreateClientLink>(
  klona(props.clientLink)
);

watch(
  computed(() => props.modelValue),
  (newValue) => {
    if (newValue) {
      editClientLink.value = klona(props.clientLink);
      loading.value = false;
      loadingDelete.value = false;
    }
  }
);

const emit = defineEmits(['update:modelValue', 'saved']);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const changed = computed(
  () => !isEqual(props.clientLink, editClientLink.value)
);

const editing = computed(() => !isEqual(props.clientLink, defaultClientLink));

const isDeleteModalVisible = ref(false);

const loading = ref(false);
const loadingDelete = ref(false);

const cancel = () => {
  if (!loading.value) {
    if (!editing.value) {
      open.value = false;
    } else if (changed.value) {
      editClientLink.value = klona(props.clientLink);
    } else {
      open.value = false;
    }
  }
};

// Advanced Client Link
const isAdvancedSelected = computed({
  get: () => editClientLink.value.isAdvanced ?? false,
  set: (newValue) => {
    editClientLink.value.isAdvanced = newValue;
  },
});

const selectedCustomFields = computed({
  get: () => editClientLink.value.sharedCustomFields ?? [],
  set: (newValue) => {
    editClientLink.value.sharedCustomFields = newValue;
  },
});

// Requests

const { $client } = useNuxtApp();
const userStore = useUserStore();
const notificationsStore = useNotifications();
const i18n = useI18n();
const apiClientLink = useApiClientLink();

const saveClientLink = async () => {
  if (
    !editClientLink.value?.outwards?.length ||
    !editClientLink.value?.inwards?.length
  ) {
    notificationsStore.createIncompleteNotification();
    return;
  }

  if (loading.value || !changed.value) {
    return;
  }

  loading.value = true;
  try {
    if (editing.value) {
      if (!('orgId' in editClientLink.value)) {
        return new Error(ErrorType.UNKNOWN);
      }
      await apiClientLink.updateById({
        ...editClientLink.value,
        isAdvanced: getChangedOrUndefined(
          props.clientLink,
          editClientLink.value,
          'isAdvanced'
        ),
        sharedCustomFields: getChangedOrUndefined(
          props.clientLink,
          editClientLink.value,
          'sharedCustomFields'
        )
          ? editClientLink.value.sharedCustomFields?.map(
            (customField) => customField._id
          )
          : undefined,
      });
    } else {
      await apiClientLink.create({
        orgId: userStore.user?.orgId ?? '',
        ...editClientLink.value,
        sharedCustomFields: editClientLink.value.sharedCustomFields?.map(
          (customField) => customField._id
        ),
      });
    }
    open.value = false;

    emit('saved');
  } catch (err) {
    loading.value = false;

    const errorMap: ErrorMapItem[] = [
      {
        type: ErrorType.NAME_ALREADY_EXISTS,
        message: i18n.t('settings.client-links.error.already-exists'),
      },
      {
        type: ErrorType.ALREADY_LINKED,
        message: i18n.t('settings.client-links.error.already-linked-update'),
      },
      ...getGeneralErrorMap(i18n),
    ];

    const messageData = getNotificationDataByMap(i18n, errorMap, err as Error, {
      title: i18n.t(
        'settings.client-links.error.could-not-add-client-link-title'
      ),
    });

    useErrorHandler(err, {
      type: 'error',
      title: messageData.title,
      message: messageData.message,
    });
  }
};

const deleteClientLink = async () => {
  if (loadingDelete.value) {
    return;
  }

  loadingDelete.value = true;
  try {
    await $client.clientLink.deleteByIds.mutate({
      ids: [(editClientLink.value as ClientLink)._id],
    });

    open.value = false;
    emit('saved');
  } catch (error) {
    if (error instanceof Error && error.message === ErrorType.ALREADY_LINKED) {
      useErrorHandler(error, {
        type: 'error',
        title: i18n.t('settings.client-links.error.already-linked-title'),
        message: i18n.t('settings.client-links.error.already-linked-message'),
      });
    } else {
      useErrorHandler(error, {
        type: 'error',
        message: i18n.t('error-messages.something-went-wrong'),
      });
    }
  } finally {
    loadingDelete.value = false;
    isDeleteModalVisible.value = false;
  }
};
</script>
